/**
 * An affiliate's referrals (TMMPDM-0042). Status filter is in the URL and sent to the server.
 * An absent optional field renders "—"; `firstPaidOn` absent is "Not yet paid". The plan column
 * is a name lookup, not a verdict: the reseller's plan name, else "Plan #{{id}}".
 */
import { useEffect, useState } from 'react';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { oneOf, useUrlState, type UrlSchema } from '@/hooks/useUrlState';
import { EmptyState, ErrorState } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Amount, stickyTableContainerSx } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import {
  AFFILIATE_REFERRAL_STATUSES,
  type AffiliateReferralRow,
  type AffiliateReferralStatus,
} from '@/api/tmm/types';
import { formatDate } from '@/format';
import { StatusChip } from '@/status';

const STATUS_FALLBACK: Record<AffiliateReferralStatus, string> = {
  ACTIVE: 'Active',
  ENDED: 'Ended',
  SUPERSEDED: 'Superseded',
  VOIDED: 'Voided',
};

type ReferralView = { status: AffiliateReferralStatus | undefined };
const VIEW: UrlSchema<ReferralView> = {
  status: { default: undefined, parse: oneOf(AFFILIATE_REFERRAL_STATUSES) },
};

interface AffiliateReferralsTableProps {
  slug: string;
  affiliateId: number;
  /** Plan id → name, from the reseller's commission plans. A lookup, not a verdict. */
  planNames: ReadonlyMap<number, string>;
}

export function AffiliateReferralsTable({
  slug,
  affiliateId,
  planNames,
}: AffiliateReferralsTableProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [view, setView] = useUrlState(VIEW);
  const status = view.status;
  const [reloadToken, setReloadToken] = useState(0);
  const [rows, setRows] = useState<AffiliateReferralRow[] | null>(null);
  const [error, setError] = useState<{ cause: unknown } | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listAffiliateReferrals(slug, affiliateId, status !== undefined ? { status } : undefined)
      .then((list) => {
        if (cancelled) return;
        setRows(list.referrals);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setRows(null);
          setError({ cause: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, affiliateId, status, reloadToken]);

  if (error) {
    return (
      <ErrorState
        error={error.cause}
        message={t('affiliates.referrals.loadError', 'Could not load referrals.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={status ?? 'ALL'}
        onChange={(_event, value: AffiliateReferralStatus | 'ALL' | null) => {
          if (value === null) return;
          setView({ status: value === 'ALL' ? undefined : value });
        }}
        aria-label={t('affiliates.referrals.filterAria', 'Referral status')}
      >
        <ToggleButton value="ALL">{t('affiliates.filter.all', 'All')}</ToggleButton>
        {AFFILIATE_REFERRAL_STATUSES.map((value) => (
          <ToggleButton key={value} value={value}>
            {t(`affiliates.referralStatus.${value}`, STATUS_FALLBACK[value])}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {rows === null ? (
        <LoadingSpinner />
      ) : (
        <ReferralTable
          rows={rows}
          filtered={status !== undefined}
          onClear={() => setView({ status: undefined })}
          planNames={planNames}
          locale={i18n.language}
        />
      )}
    </Stack>
  );
}

function ReferralTable({
  rows,
  filtered,
  onClear,
  planNames,
  locale,
}: {
  rows: AffiliateReferralRow[];
  filtered: boolean;
  onClear: () => void;
  planNames: ReadonlyMap<number, string>;
  locale: string;
}) {
  const { t } = useCopy();
  const absent = t('affiliates.absent', '—');
  return (
    <TableContainer component={Paper} variant="outlined" sx={stickyTableContainerSx}>
      <Table size="small" stickyHeader aria-label={t('affiliates.referrals.aria', 'Referrals')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('affiliates.col.subscriber', 'Subscriber')}</TableCell>
            <TableCell>{t('affiliates.col.email', 'Email')}</TableCell>
            <TableCell>{t('affiliates.col.offer', 'Offer')}</TableCell>
            <TableCell>{t('affiliates.col.since', 'Since')}</TableCell>
            <TableCell>{t('affiliates.col.firstPaid', 'First paid')}</TableCell>
            <TableCell>{t('affiliates.col.status', 'Status')}</TableCell>
            <TableCell>{t('affiliates.col.plan', 'Plan')}</TableCell>
            <TableCell align="right">{t('affiliates.col.thisPeriod', 'This period')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <EmptyState
                  title={
                    filtered
                      ? t('affiliates.referrals.filteredEmpty', 'No referrals match this filter.')
                      : t('affiliates.referrals.empty', 'No referrals yet.')
                  }
                  {...(filtered ? { filtered: { onClear } } : {})}
                />
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.attributionId} hover>
                <TableCell>{row.subscriberDisplayName ?? absent}</TableCell>
                <TableCell>{row.email ?? absent}</TableCell>
                <TableCell>{row.offerName ?? absent}</TableCell>
                <TableCell>{formatDate(row.since, locale)}</TableCell>
                <TableCell>
                  {row.firstPaidOn !== undefined
                    ? formatDate(row.firstPaidOn, locale)
                    : t('affiliates.referrals.notYetPaid', 'Not yet paid')}
                </TableCell>
                <TableCell>
                  <StatusChip
                    status={row.status}
                    label={t(
                      `affiliates.referralStatus.${row.status}`,
                      STATUS_FALLBACK[row.status],
                    )}
                  />
                </TableCell>
                <TableCell>{planLabel(row.commissionPlanId, planNames, t)}</TableCell>
                <TableCell align="right">
                  <Amount
                    minor={row.thisPeriodCommissionMinor}
                    currency={row.currency}
                    locale={locale}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function planLabel(
  id: number | undefined,
  planNames: ReadonlyMap<number, string>,
  t: (key: string, fallback?: string, values?: Record<string, string | number>) => string,
): string {
  if (id === undefined) return t('affiliates.absent', '—');
  return planNames.get(id) ?? t('affiliates.plan.fallback', 'Plan #{{id}}', { id });
}
